import SaveAltIcon from '@mui/icons-material/SaveAlt'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import Grid from '@mui/material/Grid2'
import { useWavesurfer } from '@wavesurfer/react'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import PauseIcon from '../../assets/icons/Pause.svg'
import PlayIcon from '../../assets/icons/Play.svg'
import { COLOR } from '../../constants/color.constants'

export const formatTime = (seconds: number) =>
    [seconds / 60, seconds % 60].map((v) => `0${Math.floor(v)}`.slice(-2)).join(':')

interface Props {
    url: string
    autoplay?: boolean
    downloadable?: boolean
}

const VoicePlayer: FC<Props> = ({ url, autoplay = false, downloadable = false }) => {
    const containerRef = useRef(null)
    const [isAutoPlaying, setIsAutoPlaying] = useState(false)
    const [duration, setDuration] = useState(0)
    const [isFinished, setIsFinished] = useState(false)
    const width = 'clamp(100px, 100%, 1000px)'

    const { wavesurfer, isPlaying, currentTime } = useWavesurfer({
        container: containerRef,
        height: 23,
        waveColor: '#BDBFC7',
        progressColor: COLOR.PRIMARY_MAIN,
        barWidth: 2,
        barGap: 4,
        width,
        url,
        autoplay,
    })
    useEffect(() => {
        if (wavesurfer) {
            wavesurfer.once('play', () => {
                setIsAutoPlaying(true)
            })

            wavesurfer.on('play', () => {
                setIsFinished(false)
            })

            wavesurfer.on('ready', () => {
                const duration = wavesurfer.getDuration()

                setDuration(duration)
            })

            wavesurfer.once('pause', () => {
                setIsAutoPlaying(false)
            })

            wavesurfer.once('finish', () => {
                setIsAutoPlaying(false)
                setIsFinished(true)
            })
        }
    }, [wavesurfer])

    const onPlayPause = useCallback(() => {
        if (wavesurfer) {
            wavesurfer.playPause()
        }
    }, [wavesurfer])

    const isPlayingNow = isPlaying || isAutoPlaying

    return (
        <Grid
            sx={{
                display: 'flex',
                width: '100%',
                alignItems: 'baseline',
                gap: '12px',
                padding: '12px 0',
            }}>
            <IconButton
                sx={{
                    backgroundColor: (theme) => theme.palette.primary.main,
                }}
                onClick={onPlayPause}>
                {isPlayingNow ? <PauseIcon /> : <PlayIcon />}
            </IconButton>
            <Grid
                sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                }}>
                <Grid
                    sx={{
                        width: '100%',
                    }}
                    ref={containerRef}
                />
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1px',
                    }}>
                    {!isFinished && isPlaying ? (
                        <>
                            <Typography
                                sx={{
                                    display: 'inline-block',
                                    width: '40px',
                                }}
                                variant="caption2">
                                {`${formatTime(currentTime)}`}
                            </Typography>
                            <Typography variant="caption2">/&nbsp;</Typography>
                        </>
                    ) : null}
                    <Typography
                        sx={{
                            display: 'inline-block',
                            width: '40px',
                        }}
                        variant="caption2">
                        {formatTime(duration)}
                    </Typography>
                </Box>
            </Grid>
            {downloadable ? (
                <Tooltip title="Скачать">
                    <IconButton sx={{ color: '#5A5F72', ml: 0.5 }} onClick={() => window.open(url)}>
                        <SaveAltIcon />
                    </IconButton>
                </Tooltip>
            ) : null}
        </Grid>
    )
}

export default VoicePlayer
